import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { canSearchPatientUhid } from '../../lib/patient-uhid-search';
import {
  createInvestigationBill,
  fetchInvestigationBill,
  fetchInvestigationBillLookups,
  fetchInvestigationBills,
  updateInvestigationBill,
  updateInvestigationBillPayment,
} from './investigation-bill.api';
import type { InvestigationBillListFilters, InvestigationPayment } from './types';

export const useInvestigationBills = (filters: InvestigationBillListFilters, enabled = true) => useQuery({ queryKey: ['investigation-bill', 'bills', filters], queryFn: () => fetchInvestigationBills(filters), enabled });
export const useInvestigationBillPatientBills = (uhid: string | null, sortBy: string | null = null, sortOrder: 'asc' | 'desc' = 'asc') =>
  useInvestigationBills({ page: 1, limit: 200, field: 'uhid', value: uhid ?? '', ...(sortBy ? { sortBy, sortOrder } : {}) }, Boolean(uhid));
export const useInvestigationBill = (id?: string) => useQuery({ queryKey: ['investigation-bill', 'bill', id], queryFn: () => fetchInvestigationBill(id!), enabled: Boolean(id) });
export const useInvestigationBillLookup = (type: string, search = '', departmentId = '', state = '') => useQuery({
  queryKey: ['investigation-bill', 'lookup', type, search, departmentId, state],
  queryFn: () => fetchInvestigationBillLookups(type, search, departmentId, state),
  enabled: type !== 'patients' || canSearchPatientUhid(search),
  ...(type === 'patients' ? { staleTime: 0, refetchOnMount: 'always' as const } : { staleTime: 60_000 }),
});

export const useCreateInvestigationBill = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: createInvestigationBill,
    onSuccess: () => void Promise.all([
      client.invalidateQueries({ queryKey: ['investigation-bill'] }),
      client.invalidateQueries({ queryKey: ['pathology'] }),
    ]),
  });
};

export const useUpdateInvestigationBill = (id: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof updateInvestigationBill>[1]) => updateInvestigationBill(id, payload),
    onSuccess: () => void Promise.all([
      client.invalidateQueries({ queryKey: ['investigation-bill'] }),
      client.invalidateQueries({ queryKey: ['pathology'] }),
    ]),
  });
};

export const useUpdateInvestigationBillPayment = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, paymentIndex, payload }: { id: string; paymentIndex: number; payload: { mode: InvestigationPayment['mode']; bankName: string | null; amount: number } }) =>
      updateInvestigationBillPayment(id, paymentIndex, payload),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['investigation-bill'] }),
  });
};
